import { useMemo, useState } from 'react'
import { message } from 'antd'
import { compose, hoc, useStore } from '@'
import { useForm, useFormLoading, useFormValues } from '@form'
import { withModalV4, useModal } from '@modal'
import CreateHotel from '../../actions/CreateHotel'
import UpdateHotel from '../../actions/UpdateHotel'
import { $coverageCatalogs, $hotelCoverage } from '../../stores'
import { parseIds, text } from '../CoverageCommon'

const NAME = 'HotelCoverageEditor'

const $fieldProps = {
    hotel_id: {
        rules: [
            { required: true, whitespace: true, message: 'hotel_id is required' },
            { pattern: /^\S+$/, message: 'hotel_id cannot contain spaces' }
        ]
    },
    country_code: { rules: [{ pattern: /^\s*[A-Za-z]{2}\s*$/, message: 'Two-letter ISO country code' }] },
    is_active: { valuePropName: 'checked' }
}

const countryCode = value => text(value).toUpperCase() || null

const buildCreate = values => {
    const body = { hotel_id: text(values.hotel_id), is_active: values.is_active ? 1 : 0 }
    if (countryCode(values.country_code)) {
        body.country_code = countryCode(values.country_code)
    }
    if (text(values.timezone)) {
        body.timezone = text(values.timezone)
    }
    if (typeof values.rank === 'number') {
        body.rank = values.rank
    }
    const suppliers = {}
    Object.entries(values.suppliers || {}).forEach(([column, value]) => {
        if (text(value)) {
            suppliers[column] = text(value)
        }
    })
    if (Object.keys(suppliers).length) {
        body.suppliers = suppliers
    }
    const regions = parseIds(values.regions)
    if (regions.length) {
        body.regions = regions.map(regionId => ({ region_id: regionId, supplier_code: 'EPS' }))
    }
    return body
}

// Only what changed goes out; an emptied supplier id is sent as null so the server clears it.
const buildUpdate = (values, hotel) => {
    const body = {}
    if (countryCode(values.country_code) !== countryCode(hotel.country_code)) {
        body.country_code = countryCode(values.country_code)
    }
    if ((text(values.timezone) || null) !== (text(hotel.timezone) || null)) {
        body.timezone = text(values.timezone) || null
    }
    if (typeof values.rank === 'number' && values.rank !== hotel.rank) {
        body.rank = values.rank
    }
    if ((values.is_active ? 1 : 0) !== (hotel.is_active ? 1 : 0)) {
        body.is_active = values.is_active ? 1 : 0
    }
    const current = {}
    hotel.suppliers.forEach(item => {
        current[item.column] = text(item.supplier_hotel_id) || null
    })
    const suppliers = {}
    Object.entries(values.suppliers || {}).forEach(([column, value]) => {
        const next = text(value) || null
        if (next !== (current[column] || null)) {
            suppliers[column] = next
        }
    })
    if (Object.keys(suppliers).length) {
        body.suppliers = suppliers
    }
    return body
}

const container = compose(
    hoc(props => {
        const { $modal, isEditMode } = props

        const [catalogs] = useStore([$coverageCatalogs])
        const [filter, setFilter] = useState('')

        const [$props, $form] = useForm(
            ({ form }) => {
                const showConflict = (e, values) => {
                    if (e.status !== 409) {
                        return
                    }
                    const { conflict } = e.data
                    if (conflict && conflict.column) {
                        setFilter('')
                        form.setFields([
                            {
                                name: ['suppliers', conflict.column],
                                errors: [`Already used by hotel ${conflict.hotel_id}`]
                            }
                        ])
                        form.scrollToField(['suppliers', conflict.column])
                    } else if (values.mode !== 'edit') {
                        form.setFields([{ name: 'hotel_id', errors: [e.message] }])
                    }
                }

                const onSubmit = async values => {
                    try {
                        if (values.mode === 'edit') {
                            const hotel = $hotelCoverage.getState()
                            const body = buildUpdate(values, hotel)
                            if (!Object.keys(body).length) {
                                message.info('Nothing changed')
                                $modal.close()
                                return
                            }
                            await UpdateHotel(hotel.hotel_id, body)
                        } else {
                            await CreateHotel(buildCreate(values))
                        }
                        $modal.close()
                    } catch (e) {
                        showConflict(e, values)
                        throw e
                    }
                }

                return {
                    name: NAME,
                    onSubmit,
                    props: { colon: false, layout: 'vertical', wrapperCol: { flex: 1 } }
                }
            },
            [$modal]
        )

        const $loading = useFormLoading($form.name)

        $modal.setForm($form)
        $modal.setLoading($loading)

        const shownColumns = useMemo(() => {
            const keyword = text(filter).toLowerCase()
            const shown = {}
            ;(catalogs || []).forEach(catalog => {
                shown[catalog.column] =
                    !keyword ||
                    catalog.column.toLowerCase().includes(keyword) ||
                    catalog.codes.some(code => code.toLowerCase().includes(keyword))
            })
            return shown
        }, [catalogs, filter])

        return {
            catalogs: catalogs || [],
            shownColumns,
            filter,
            setFilter,
            $form,
            $props,
            $loading,
            $fieldProps,
            isEditMode,
            ...props
        }
    }),
    withModalV4(props => {
        const $values = useFormValues(NAME)

        const isEditMode = useMemo(() => {
            return !!($values && $values.mode === 'edit')
        }, [$values])

        const hotelId = $values && $values.hotel_id

        const [$props, $modal] = useModal(
            () => ({
                name: NAME,
                props: {
                    title: isEditMode ? `Edit hotel ${hotelId}` : 'New hotel',
                    width: 960,
                    destroyOnClose: true,
                    okText: isEditMode ? 'Save' : 'Create'
                }
            }),
            [isEditMode, hotelId]
        )

        return {
            $modal,
            $props,
            isEditMode,
            ...props
        }
    })
)

export default container
